import {
  SUBJECT_TAGS_ACCOUNTS_PAYABLE,
  Transaction,
  type TransactionAccountList,
  TransactionActivity,
  Vendor,
} from '@kalos/kalos-rpc';
import Alert from '@mui/material/Alert';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { type FC, useCallback, useState } from 'react';

import { queryKeys } from '../../../hooks/react-query/constants';
import {
  getFileExt,
  SUBJECT_TAGS_TRANSACTIONS,
  timestamp,
  TransactionActivityClientService,
  TransactionClientService,
  uploadPhotoToExistingTransaction,
  VendorClientService,
} from '../../../tools/helpers';
import { Alert as AlertComponent } from '../Alert';
import { Confirm } from '../Confirm';
import { Form, type Schema } from '../Form';
import { type RoleType } from '../Payroll';
interface Props {
  loggedUserId: number;
  title?: string;
  onClose: (() => void) | null;
  onUpload?: (() => void) | null;
  defaultTag?: string;
  defaultPurchase?: number;
  costCenters: TransactionAccountList;
  fullWidth?: boolean;
  role?: RoleType;
}

type Entry = {
  file?: string;
  description: string;
  eventId: number;
  tag: string;
  // stringifies id
  vendor: string;
  cost: number;
  ownerId: number;
  costCenter: number;
  department: number;
  orderNumber: string;
  invoiceNumber: string;
};
export type CostCenter = {
  value: number;
  label: string;
};
export const UploadPhotoTransaction: FC<Props> = ({
  onClose,
  loggedUserId,
  costCenters,
  title = 'Upload/Create Transaction For Non Credit Card Receipts',
  defaultTag = 'PickTicket',
  defaultPurchase = 'Fuel',
  fullWidth = true,
  role,
  onUpload,
}) => {
  const [fileData, setFileData] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [stringError, setStringError] = useState<string | undefined>();
  const [, setOrdeNumberCheck] = useState<string>('');
  const [, setInvoiceCheck] = useState<string>('');

  const queryClient = useQueryClient();

  const [validateJobNumber, setValidateJobNumber] = useState<Entry | undefined>(undefined);
  const [nameValidationError, setNameValidationError] = useState<string | undefined>(undefined);
  const temp = costCenters.results.map((entry) => {
    return {
      value: entry.id,
      label: entry.description,
    };
  });
  const [formData, setFormData] = useState<Entry>({
    file: '',
    description: '',
    eventId: 0,
    department: 0,
    costCenter: (
      temp.find(({ label }) => label === defaultPurchase) || {
        value: 0,
      }
    ).value,
    ownerId: loggedUserId,
    vendor: '0',
    cost: 0,
    tag: (
      SUBJECT_TAGS_TRANSACTIONS.find(({ label }) => label === defaultTag) || {
        value: '',
      }
    ).value,
    orderNumber: '',
    invoiceNumber: '',
  });
  const [formKey, setFormKey] = useState<number>(0);
  const handleFileLoad = useCallback((fileData: string) => setFileData(fileData), [setFileData]);

  // TODO reminding myself to fix this in a bit, we don't need to switch between Entry and Transaction, we can just use Transaction
  // ! Also no need to create a new Transaction client when we can reuse the one from Helpers
  const handleSubmit = useCallback(
    async (data: Entry) => {
      if (data.description.includes('/') || data.description.includes('\\')) {
        setNameValidationError('The description field cannot contain the characters "/" or "\\".');
        setError(true);
        return;
      }
      setSaved(false);
      setError(false);
      setSaving(true);
      const newTransaction = Transaction.create();
      newTransaction.jobId = data.eventId;
      const type = data.tag.replace('Subject=', '');
      newTransaction.vendorCategory = type;
      newTransaction.statusId = 2;
      newTransaction.isActive = 1;
      newTransaction.ownerId = data.ownerId;
      newTransaction.assignedEmployeeId = loggedUserId;
      newTransaction.amount = data.cost;
      newTransaction.description = data.description;
      newTransaction.vendorId = Number(data.vendor);

      if (newTransaction.vendorId != 0) {
        const vendorReq = Vendor.create();
        vendorReq.isActive = 1;
        vendorReq.id = Number(newTransaction.vendorId);
        const result = await VendorClientService.Get(vendorReq);
        newTransaction.vendor = result!.vendorName;
      }
      newTransaction.costCenterId = data.costCenter;
      newTransaction.timestamp = timestamp();
      newTransaction.isRecorded = true;
      newTransaction.departmentId = data.department;
      newTransaction.orderNumber = data.orderNumber;
      newTransaction.invoiceNumber = data.invoiceNumber;

      let insert: Transaction | undefined = undefined;
      try {
        insert = await TransactionClientService.Create(newTransaction);
        queryClient
          .invalidateQueries({
            queryKey: [queryKeys.transaction.root],
          })
          .catch(console.error);
      } catch (err) {
        console.error(`An error occurred while uploading a transaction: ${err}`);
        try {
          const log = TransactionActivity.create();
          log.timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
          log.userId = loggedUserId;
          log.statusId = 2;
          log.isActive = 1;
          log.description = `ERROR : An error occurred while uploading a transaction: ${err}. Vendor: ${data.vendor}`;
          await TransactionActivityClientService.Create(log);
        } catch (err) {
          console.error(
            `An error occurred while uploading a transaction activity log for an error in an S3 bucket: ${err}`,
          );
        }
      }

      if (!insert) {
        console.error(
          `No transaction to upload a photo for, as the call to TransactionClientService.Create was unsuccessful. Returning.`,
        );
        return;
      }

      if (data.file) {
        const ext = getFileExt(data.file);
        const name = `${insert!.id}-${data.description}-${Math.floor(Date.now() / 1000)}.${ext}`;
        await uploadPhotoToExistingTransaction(
          name,
          type === 'PickTicket' || type === 'Receipt' || type === 'Invoice' ? type : undefined,
          fileData,
          insert,
          loggedUserId,
          1,
        );
        setSaving(false);
        setSaved(true);
        setFormKey(formKey + 1);
      } else {
        setSaving(false);
        setSaved(true);
        setFormKey(formKey + 1);
      }

      if (onUpload) onUpload();
    },
    [loggedUserId, onUpload, queryClient, fileData, formKey],
  );
  function uniq(a: { label: string; value: string }[]) {
    const newArray: { label: string; value: string }[] = [];
    for (let i = 0; i < a.length; i++) {
      const temp = newArray.find((b) => b.value === a[i].value && b.label === a[i].label);
      if (temp == undefined) {
        newArray.push(a[i]);
      }
    }
    return newArray;
  }
  const handleSetValidateJobNumber = useCallback(
    (validate: Entry | undefined) => setValidateJobNumber(validate),
    [setValidateJobNumber],
  );
  const handleCheckOrderNumber = useCallback(async (orderNumber: string) => {
    if (orderNumber != '') {
      console.log('handleCheck Order number');
      const transactionReq = Transaction.create();
      transactionReq.orderNumber = orderNumber;
      transactionReq.vendorCategory = "'PickTicket','Receipt','Invoice'";
      transactionReq.isActive = 1;
      try {
        const result = await TransactionClientService.Get(transactionReq);
        if (result.id != 0) {
          setStringError(
            `This Order Number already exists. You can still create this transaction,
        but it may result in duplicate transactions. It is recommended that you
        search for the existing transaction and update it.`,
          );
        }
      } catch {
        setStringError(undefined);
      }
    }
  }, []);
  const handleCheckInvoiceNumber = useCallback(async (invoiceNumber: string) => {
    if (invoiceNumber != '') {
      console.log('we check invoice');
      const transactionReq = Transaction.create();
      transactionReq.invoiceNumber = invoiceNumber;
      transactionReq.vendorCategory = "'PickTicket','Receipt','Invoice'";
      transactionReq.isActive = 1;
      try {
        const result = await TransactionClientService.Get(transactionReq);
        if (result.id != 0) {
          setStringError(
            `This Invoice Number already exists. You can still create this transaction,
        but it may result in duplicate transactions. It is recommended that you
        search for the existing transaction and update it.`,
          );
        }
      } catch {
        setStringError(undefined);
      }
    }
  }, []);

  const handleSetInvoiceNumberToCheckDuplicate = useCallback(
    async (invoice: string) => {
      setInvoiceCheck(invoice);
      handleCheckInvoiceNumber(invoice);
    },
    [handleCheckInvoiceNumber],
  );
  const handleSetOrderNumberToCheckDuplicate = useCallback(
    async (orderNumber: string) => {
      setOrdeNumberCheck(orderNumber);
      handleCheckOrderNumber(orderNumber);
    },
    [handleCheckOrderNumber],
  );

  const handleValidate = useCallback(
    (entry: Entry) => {
      if (!entry.eventId) {
        handleSetValidateJobNumber(entry);
        return;
      }
      handleSubmit(entry);
    },
    [handleSetValidateJobNumber, handleSubmit],
  );

  const SCHEMA: Schema<Entry> = [
    [
      {
        name: 'file',
        label: 'Photo',
        type: 'file',
        onFileLoad: handleFileLoad,
      },
    ],
    [
      {
        name: 'tag',
        label: 'File Type',
        required: role != 'AccountsPayable',
        options: uniq(SUBJECT_TAGS_ACCOUNTS_PAYABLE),
      },
    ],
    [
      {
        name: 'description',
        label: 'Description',
        required: role != 'AccountsPayable',
      },
    ],
    [
      {
        name: 'orderNumber',
        label: 'Purchase Order #',
        required: true,
        onBlur: handleSetOrderNumberToCheckDuplicate,
      },
      {
        name: 'invoiceNumber',
        label: 'Invoice #',
        onBlur: handleSetInvoiceNumberToCheckDuplicate,
      },
    ],
    [
      {
        name: 'ownerId',
        label: 'Purchaser ',
        type: 'technician',
      },
    ],
    [
      {
        name: 'eventId',
        label: 'Job Number',
        type: 'eventId',
      },
    ],
    [
      {
        name: 'department',
        label: 'Department',
        type: 'department',
        required: true,
      },
    ],
    [
      {
        name: 'vendor',
        label: 'Vendor',
        type: 'vendor',
        validationOnSave(value) {
          return !Number(value) ? 'This field is required' : '';
        },
        required: true,
      },
      {
        name: 'costCenter',
        label: 'Cost Center',
        type: 'number',
        options: costCenters.results.map((center) => ({
          label: `${center.description} (${center.id})`,
          value: center.id,
        })),
        required: true,
      },
    ],
    [
      {
        name: 'cost',
        label: 'Cost',
        type: 'number',
        invisible: formData.tag != 'Subject=Receipt' && formData.tag != 'Subject=Invoice',
      },
    ],
  ] as Schema<Entry>;
  console.log('its opening');
  return (
    <>
      {validateJobNumber && (
        <Confirm
          className="Job-Error-Confirm"
          title="No Job Number Set"
          open={validateJobNumber !== undefined}
          onClose={() => handleSetValidateJobNumber(undefined)}
          onConfirm={() => {
            handleSubmit(validateJobNumber);
            handleSetValidateJobNumber(undefined);
          }}
        >
          Are you sure that this transaction does not require a job number?
        </Confirm>
      )}
      {stringError && (
        <AlertComponent
          open={stringError != undefined}
          onClose={() => setStringError(undefined)}
          title="Error"
        >
          {stringError}
        </AlertComponent>
      )}
      <Form<Entry>
        key={formKey + formData.tag}
        title={title}
        schema={SCHEMA}
        className="transation-form select-fixed"
        data={formData}
        onClose={onClose}
        onSave={(saved) => handleValidate(saved)}
        onChange={setFormData}
        submitLabel="Create Transaction"
        cancelLabel="Close"
        disabled={saving}
        intro={
          saved && (
            <Alert severity="success" className="-m-4">
              <big>Transaction Created!</big>
              <br />
              You can upload another file.
            </Alert>
          )
        }
        error={
          error &&
          (nameValidationError
            ? nameValidationError
            : 'Error while uploading file. Please try again.')
        }
        fullWidth={fullWidth}
      />
    </>
  );
};
